import { InfoCircleOutlined } from '@ant-design/icons';
import { Plus } from 'lucide-react';
import {
  type CSSProperties,
  memo,
  type ReactElement,
  type ReactNode,
  useMemo,
} from 'react';
import styled from 'styled-components';

import { CenterFlex, Flex, VFlex } from '@/components/base';
import Button from '@/components/Button/Button';
import Tooltip from '@/components/Tooltip/TooltipV2';
import Typo from '@/components/Typography/Typo';
import { getAlphaColor } from '@/styles/colors/color.utils';

type Props<T> = {
  value: T;
  icon?: ReactNode;
  title: string;
  description: string;
  onClick?: (value: T) => void;
  style?: CSSProperties;
  customButton?: ReactNode;
  disabled?: boolean;
  addonText?: string;
  tooltip?: {
    node: ReactNode;
  };
};

const QuickActionItem = <T extends unknown>(props: Props<T>) => {
  const button: ReactNode = useMemo(() => {
    if (props.customButton) return props.customButton;
    if (props.disabled) return null;
    return (
      <Button
        sizeType="l"
        style={{ width: 40, height: 40, borderRadius: 40 }}
        onClick={() => props.onClick?.(props.value)}
        leftIcon={<Plus />}
      />
    );
  }, [props]);

  const disabledStyle: CSSProperties = useMemo(() => {
    if (props.disabled)
      return {
        opacity: 0.5,
        cursor: 'not-allowed',
      };
    return {};
  }, [props.disabled]);

  return (
    <Style
      onClick={() => props.onClick?.(props.value)}
      j="space-between"
      style={{
        ...disabledStyle,
        ...props.style,
      }}
    >
      <VFlex g={8}>
        <Flex a="center" g={8}>
          {props.icon && <SIconWrapper>{props.icon}</SIconWrapper>}
          <Typo type="label2" weight="sb" className="title">
            {props.title}
          </Typo>
          {props.tooltip && (
            <Tooltip
              tooltipNode={
                <div style={{ whiteSpace: 'wrap', width: 160 }}>
                  {props.tooltip.node}
                </div>
              }
            >
              <CenterFlex className="ic-info">
                <InfoCircleOutlined />
              </CenterFlex>
            </Tooltip>
          )}
        </Flex>
        <Typo
          type="body3"
          weight="r"
          className="desc"
          style={{ lineHeight: 1.4 }}
        >
          {props.description}
        </Typo>
      </VFlex>
      <Flex a="flex-end" j="space-between">
        <Typo type="body3" weight="m" className="addon-text">
          {props.addonText}
        </Typo>
        {button}
      </Flex>
    </Style>
  );
};

const Style = styled(VFlex)`
  gap: 12px;
  width: 300px;
  padding: 20px;
  border-radius: 6px;
  transition: all 0.2s;
  cursor: pointer;
  background-color: ${(props) =>
    getAlphaColor(props.theme.colors.primary100, 0.04)};

  .title {
    color: ${(props) => props.theme.colors.gray800};
  }
  .desc {
    color: ${(props) => props.theme.colors.gray700};
  }
  .addon-text {
    color: ${(props) => props.theme.colors.gray600};
  }
  .ic-info {
    font-size: 16px;
    color: ${(props) => props.theme.colors.gray500};
  }
`;

const SIconWrapper = styled(CenterFlex)`
  color: ${(props) => props.theme.colors.gray700};
  background-color: ${(props) => props.theme.colors.gray300};
  border-radius: 20px;
  width: 28px;
  height: 28px;
  font-size: 16px;
`;

export default memo(QuickActionItem) as <T>(
  props: Props<T>,
) => ReactElement;
